import { React, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  FormControl,
  IconButton,
  InputAdornment,
  Button,
  TextField,
  // FormControlLabel,
  // Checkbox,
  Box,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import logoIcon from '../../../../assets/common-assets/docu-transcribe-logo(1).png';
import wavingHand from '../../../../assets/common-assets/wavingHand.png';
import Visibility from '../../../../assets/common-assets/eye.svg';
import VisibilityOff from '../../../../assets/common-assets/eye-off.svg';
import '../common/commonCss.css';
import { Root, classes } from './utils';
import { loginUser, getLoggedInUserDetail } from '../../../../services/userApi';
import { getLoggedInUser } from '../../../../store/userSlice';
import { lightPalette } from '../../../../theme';
import {
  LOGIN,
  getHelperTextForEmptyField,
} from '../../../../constants/staticContent';
import ROLES from '../../../../constants/roles';
import Toast from '../../utils/Toast';
import { OTP_VERIFICATION_TOAST } from '../../../../constants/toastConstant';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import AuthenticationNewLeftGrid from '../common/AuthenticationNewLeftGrid';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });

  const validateEmptyField = (text) => text !== '';

  const handleFormData = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: value.trim() === '',
      }));
    } else if (name === 'password') {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: value.trim() === '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const passwordValid = validateEmptyField(formData.password);
    const emailValid = validateEmptyField(formData.email);
    setErrors({
      email: !emailValid,
      password: !passwordValid,
    });
    if (emailValid && passwordValid) {
      setIsLoading(true);
      const data = {
        email: formData.email.trim(),
        password: formData.password.trim(),
      };
      const user = await loginUser(data);
      if (user && Object.keys(user).length > 0) {
        setIsLoading(false);
      }
      if (user?.otp) {
        const email = getLocalStorageItem('email');
        navigate('/otp-auth');
        Toast({
          message: `${OTP_VERIFICATION_TOAST.MESSAGE} to ${email}`,
          variant: 'success',
        });
      } else {
        const roleData = await user?.roles[0];
        if (user) {
          const loggedInUser = await getLoggedInUserDetail();
          dispatch(getLoggedInUser(loggedInUser));
        }
        if (
          user &&
          (roleData === ROLES.SUPER_ADMIN || roleData === ROLES.SUPER_MANAGER)
        ) {
          navigate('/dashboard');
        } else if (user && roleData === ROLES.SALES_ADMIN) {
          navigate('/sales-admin-dashboard');
        } else if (user && roleData === ROLES.ADMIN) {
          navigate('/admin-dashboard');
        } else if (user && roleData === ROLES.SALES) {
          navigate('/subscriber');
        } else if (user) {
          navigate('/invoices');
        }
        setIsLoading(false);
      }
    }
    // }
  };

  return (
    <Root>
      <Grid container component="main" className={classes.containerMainGrid}>
        {/* this is common left grid for all authentication pages */}
        {/* <AuthenticationLeftGrid /> */}
        <AuthenticationNewLeftGrid />

        <Grid
          item
          xs={12}
          sm={6}
          md={6.5}
          lg={7.5}
          className={classes.rightGrid}
        >
          <Box className={classes.rightGridMainBox}>
            <Box
              className={classes.logoBox}
              component="img"
              src={logoIcon}
              alt="logoIcon"
            />

            <Typography className={classes.welcomeHeader} variant="h4">
              {LOGIN.WELCOME_TEXT.TITLE}
              <Box
                component="img"
                className="waving-hand"
                src={wavingHand}
                alt="wavingHand"
              />
            </Typography>

            <Typography
              variant="subtitle1"
              className={classes.rightBoxInfoSubtitle}
              color={lightPalette.carbonFiber.semiDark}
            >
              {LOGIN.WELCOME_TEXT.INFO}
            </Typography>

            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              className={classes.submitButton}
            >
              <FormControl className={classes.formControl}>
                <TextField
                  className={classes.formFirstTextField}
                  required
                  id="email"
                  label={LOGIN.TEXTFIELD.EMAIL}
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  autoFocus
                  onChange={handleFormData}
                  error={errors.email}
                  helperText={getHelperTextForEmptyField(
                    'email',
                    'email',
                    errors,
                    formData,
                  )}
                />

                <TextField
                  className={classes.formSecondTextField}
                  required
                  name="password"
                  label={LOGIN.TEXTFIELD.PASSWORD}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={handleFormData}
                  error={errors.password}
                  helperText={getHelperTextForEmptyField(
                    'password',
                    'password',
                    errors,
                    formData,
                  )}
                  autoComplete="password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? (
                            <Box
                              component="img"
                              className={classes.eyeIconForPassword}
                              src={Visibility}
                              alt="EyeOnIcon"
                            />
                          ) : (
                            <Box
                              component="img"
                              className={classes.eyeIconForPassword}
                              src={VisibilityOff}
                              alt="EyeOffIcon"
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box className={classes.formFirstInnerBox}>
                  {/* <FormControlLabel
                    control={(
                      <Checkbox
                        value="remember"
                      />
                    )}
                    label={(
                      <Typography
                        variant="subtitle1"
                        color={lightPalette.shoeWax.semiLight}
                      >
                        {LOGIN.REMEMBER_ME}
                      </Typography>
                    )}
                  /> */}

                  <Typography
                    variant="subtitle1"
                    className={classes.forgotPass}
                  >
                    <Link
                      to="/resetpassword"
                      className={classes.forgotPassLink}
                    >
                      {LOGIN.FORGOT_PASSWORD}
                    </Link>
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  className={classes.formButtons}
                >
                  {isLoading ? (
                    <Box>
                      <CircularProgress
                        className={classes.loaderBox}
                        size={20}
                      />
                    </Box>
                  ) : (
                    <Typography variant="h5">{LOGIN.LOGIN_BUTTON}</Typography>
                  )}
                </Button>

                {/* <Divider variant="middle">Or</Divider> */}

                {/* <Button
                  className={classes.formButtons}
                  variant="contained"
                  sx={{
                    backgroundColor: "rgb(255, 255, 255)",
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    color: "rgba(30, 30, 30, 0.6)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    className={classes.formGoogleLogoImg}
                    src={googleLogo}
                    alt="googleLogo"
                  />
                  <Typography variant="h5">Sign in with Google</Typography>
                </Button> */}

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: '400',
                    textAlign: 'center',
                    color: lightPalette.shoeWax.semiLight,
                  }}
                >
                  {LOGIN.SIGNUP_LINK.TEXT}

                  <Link
                    to="/register"
                    style={{
                      color: lightPalette.primary.main,
                      fontWeight: '500',
                      textDecoration: 'none',
                    }}
                  >
                    {LOGIN.SIGNUP_LINK.LINK}
                  </Link>
                </Typography>
              </FormControl>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Root>
  );
};

export default Login;
